import { useState } from "react";
import { PageHeader, Disclaimer } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Award, ShieldCheck, CheckCircle2 } from "lucide-react";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please accept the consent statement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/contact", {
        ...form,
        consent: true,
        consent_timestamp: new Date().toISOString(),
      });
      setDone(true);
      toast.success("Message sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Speak with a Registered Migration Agent."
        subtitle="Tell us about your enquiry. We respond within 1 business day."
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          {/* Contact details */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#F8F9FA] border border-slate-200 p-7">
              <h3 className="font-display text-2xl text-[#29324A] mb-5">Direct contact</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-1 text-[#C43A89]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-0.5">Email</div>
                    <a href="mailto:yasmin@studynet.com.au" className="text-[#29324A] font-semibold hover:text-[#29324A] break-all">yasmin@studynet.com.au</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-1 text-[#C43A89]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-0.5">Business</div>
                    <div className="text-[#29324A] font-semibold">StudyNet Pty Ltd</div>
                    <div className="text-slate-600 text-sm">Level 12, Suite 1201, 370 Pitt Street<br/>Sydney NSW 2000, Australia</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-1 text-[#C43A89]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-0.5">Calls</div>
                    <div className="text-[#29324A] font-semibold">By appointment</div>
                    <a href="/book-consultation" className="text-[#29324A] text-xs font-semibold hover:underline">Book a consultation →</a>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-[#29324A] text-white p-7 relative grain">
              <div className="relative">
                <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-3">Our Migration Team</div>
                <h3 className="font-display text-2xl mb-4">4 Registered Migration Agents</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Award className="h-4 w-4 text-[#C43A89]" /> MARN 2619415 | IAA 202603837</div>
                  <div className="flex items-center gap-2"><Award className="h-4 w-4 text-[#C43A89]" /> MARN 2518784 · 2619194</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C43A89]" /> Migration Institute of Australia</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89]" /> StudyNet Pty Ltd</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="bg-[#F8F9FA] border border-slate-200 p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-[#C43A89] mx-auto mb-5" />
                <h3 className="font-display text-3xl text-[#29324A] mb-3">Message received.</h3>
                <p className="text-slate-600">We'll respond by email within 1 business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name *"><Input data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
                  <Field label="Email *"><Input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
                  <Field label="Phone"><Input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                  <Field label="Subject"><Input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="e.g. Skilled visa enquiry" /></Field>
                </div>
                <Field label="Message *">
                  <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => update("message", e.target.value)} />
                </Field>
                <Disclaimer>
                  Please do not share sensitive personal details (passport numbers, financial details) via this contact form. Use a paid consultation for case-specific advice.
                </Disclaimer>
                <ConsentCheckbox
                  value={consent}
                  onChange={setConsent}
                  testid="contact-consent"
                  showDisclaimer={false}
                />
                <Button data-testid="contact-submit-btn" type="submit" disabled={submitting || !consent} className="w-full bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none py-6 font-semibold text-base btn-shimmer disabled:opacity-50">
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
